import { useEffect, useMemo, useState } from "react";
import type { NextPage, NextPageContext } from "next";
import getConfig from "next/config";
import Head from "next/head";
import { useRouter } from "next/router";
import { useQuery } from "@tanstack/react-query";
import AdSlot from "@pageContent/AdSlot";
import OopsPage from "@pageContent/OopsPage";
import PaginatedOverview from "@pageContent/PaginatedOverview";
import VideoHeader from "@pageContent/VideoHeader";
import PageContainer from "@pageContent/common/PageContainer";
import getHostConfig, { useHostConfig } from "@utils/common/getHostConfig";
import { useNoticeError } from "@utils/common/newRelic";
import { getDateLabel } from "@utils/pageContent/dateLabel";
import { fetchDpgAds, injectSnippet, useDeviceType } from "@utils/pageContent/displayAds";
import { fetchByEndpoint } from "@utils/pageContent/fetchData";
import useFetchLimit from "@utils/pageContent/useFetchLimit";

const initialFetchedItemsNumber = {
  xs: 8,
  sm: 8,
  md: 12,
  lg: 16,
};

const collectionTitle = "Fragmenten";

const {
  publicRuntimeConfig: { cloudinaryDomain },
} = getConfig();

const fetchVideo = async (contentApiEndpoint: string, videoSlug?: string | string[]) =>
  fetchByEndpoint<Video | null>(`${contentApiEndpoint}/video/${videoSlug}`).then((video) =>
    video ? { ...video, dateLabel: getDateLabel(video.createdAt) } : null
  );

const VideoPage: NextPage<{ initialVideo?: Video; initialAds?: DpgAdsResponse | null }> = ({
  initialAds,
  initialVideo,
}) => {
  const limit = useFetchLimit(initialFetchedItemsNumber);
  const { outletName, contentApiEndpoint } = useHostConfig();
  const { query, asPath } = useRouter();
  const deviceType = useDeviceType();
  const [videoSlug] = useMemo(() => (Array.isArray(query.slugs) ? [...query.slugs].reverse() : []), [query]);
  const [activeCategory, setActiveCategory] =
    useState<Pick<CategoryEntry, "title" | "slug" | "videoEndpoint" | "articleEndpoint">>();
  const [page, setPage] = useState(1);

  const videoQueryKey = ["video", contentApiEndpoint, videoSlug];

  const {
    isLoading,
    data: video,
    isError,
    error,
  } = useQuery<Video | null>({
    queryKey: videoQueryKey,
    queryFn: async () => fetchVideo(contentApiEndpoint, videoSlug),
    initialData: initialVideo,
  });

  useNoticeError(error, {
    queryKey: videoQueryKey.join(","),
    outletName,
    contentApiEndpoint,
    videoSlug,
  });

  useEffect(() => {
    const onCategoryChange = ({ category }: WebXEventParams["categoryChange"]) => {
      setActiveCategory({
        ...category,
        articleEndpoint: "",
        videoEndpoint: `${contentApiEndpoint}/videos/${category.slug}`,
      });
    };
    if (video) {
      const mainCategory = video.categories.slice(-1).pop();
      if (mainCategory && activeCategory?.slug !== mainCategory.slug) onCategoryChange({ category: mainCategory });
    }
    window.eventBus.on("categoryChange", onCategoryChange);
    return () => {
      window.eventBus.off("categoryChange", onCategoryChange);
    };
  }, [video, contentApiEndpoint]);

  useEffect(() => {
    if (asPath.includes("zoek=")) return;
    if (initialAds && !window.dpgPreviousRequest) {
      window.dpgPreviousRequest = initialAds.requestBody;
      injectSnippet(initialAds.html);
      window.eventBus.dispatch("triggerAd", { hasInitialAds: true });
      return;
    }

    fetchDpgAds(asPath).then((data) => {
      if (data) {
        injectSnippet(data.html);
        window.eventBus.dispatch("triggerAd", {});
      }
    });
  }, [initialAds, asPath]);

  if (isError) return <OopsPage />;

  if (!video && !isLoading) return <OopsPage is404 />;
  const { structuredData } = video || {};
  return (
    <>
      <Head>
        {video && <title>{video.title}</title>}

        {!!structuredData && (
          <>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(structuredData),
              }}
            />
            <meta
              property="og:image"
              content={`${cloudinaryDomain}/image/fetch/w_1200,h_627,c_fill,q_auto,f_auto/${structuredData.thumbnailUrl}`}
            />
            <meta
              property="og:url"
              content={
                typeof structuredData.mainEntityOfPage === "string"
                  ? structuredData.mainEntityOfPage
                  : structuredData.mainEntityOfPage?.url
              }
            />
            <meta property="og:title" content={structuredData.headline} />
            <meta property="og:type" content="video.other" />
          </>
        )}
      </Head>

      <VideoHeader video={video} isShortFormPage activeCategorySlug={activeCategory?.slug} autoplay />
      <PageContainer>
        <PaginatedOverview
          category={activeCategory}
          limit={limit}
          page={page}
          onPageChange={setPage}
          collectionTitle={activeCategory ? collectionTitle : " "}
          isVideoOverview
          activeId={video?.videoGuid}
        />
        {deviceType && <AdSlot id={`${deviceType}-v2`} />}
      </PageContainer>
    </>
  );
};

VideoPage.getInitialProps = async ({ req, res, query }: NextPageContext) => {
  if (!res || !req) return {};
  const [videoSlug] = Array.isArray(query.slugs) ? [...query.slugs].reverse() : [];

  try {
    const { contentApiEndpoint } = getHostConfig();
    const initialVideo = await fetchVideo(contentApiEndpoint, videoSlug);

    if (initialVideo) {
      const [currentUrl] = req.url?.split("?") || [];
      const initialAds = await fetchDpgAds(currentUrl);
      if (currentUrl !== initialVideo.url) {
        // Redirect to correct URL
        res.writeHead(301, { Location: initialVideo.url });
        res.end();
        return {};
      }
      return { initialVideo, initialAds };
    }
    res.statusCode = 404;
  } catch (error) {
    console.log(error);
    res.statusCode = 500;
  }
  return {};
};

export default VideoPage;
